/*
 * app.js — routing (the one-page home, the product page), home sections (anchors, header, "Commander maintenant" cursor), photo gallery + lightbox, product UI, cart, drawers (cart, sizes, menu, newsletter).
 * Classic script so the site also works when index.html is opened from disk.
 */
(function () {
  'use strict';

  // On Shopify the HTML carries the page asked for, and the two others inside <template data-page> : search engines and AI crawlers
  // read one page per address. They are put back in place before anything else, so the site keeps its three pages.
  document.querySelectorAll('template[data-page]').forEach((t) => t.replaceWith(t.content.cloneNode(true)));

  /* ================================================================== */
  /* Data                                                                */
  /* ================================================================== */

  const PRODUCT = {
    id: 'chemise-vague',
    name: 'La Chemise Blanche',
    price: 90,                                                      // shipping included (the Shopify variants must say 90 too)
    colors: [
      { id: 'blanc', label: 'Blanc jacquard', hex: '#f3f2ee', photo: 0 },
      // The brand has one model for now : the white shirt. The striped prototype is kept here, switched off ; its 4 Shopify variants
      // were deleted on 11 Oct 2026. To sell it again : recreate them in Shopify, add their ids to PRODUCT.shopify.variants, uncomment this line.
      // { id: 'raye', label: 'Rayé lilas', hex: '#e8dcec', stripe: true, photo: 7 },
    ],
    sizes: ['S', 'M', 'L', 'XL'],
    // measures of the garment, flat, in cm : one value per size, in the order of `sizes`
    sizeTable: [
      { label: '½ tour de poitrine', v: ['57,5', '59,5', '62,5', '65,5'] },
      { label: '½ tour de taille', v: ['54', '56', '59', '62'] },
      { label: '½ tour de hanche', v: ['54', '56', '59', '62'] },
      { label: 'Longueur milieu dos', v: ['73', '74,5', '76', '77,5'] },
      { label: 'Longueur milieu devant', note: 'point le plus court', v: ['57,5', '59', '60,5', '62,5'] },
    ],
    // Home page 3D model. null = procedural shirt. After exporting the CLO garment as a GLB,
    // put it in assets/models/chemise.glb and set this to 'assets/models/chemise.glb'.
    glb: null,
    // Shopify : store "ïaz", product "La Chemise Blanche", handle "chemise-vague" (gid://shopify/Product/16076488409432).
    // Checkout goes through a cart permalink : /cart/<variant id>:<qty>,… on the shop's own domain (the site is its theme) ;
    // from the local dev server, through the myshopify address.
    shopify: {
      shop: 'tmacsc-8h.myshopify.com',
      handle: 'chemise-vague',
      // Public Storefront API token (Shopify admin › Headless channel). Empty = every size shows as available.
      storefrontToken: 'b57aa588f5652e9074e440e7c1a096b4',
      apiVersion: '2026-07',
      variants: {
        'blanc:S': '58673301717336', 'blanc:M': '58673301750104', 'blanc:L': '58673301782872', 'blanc:XL': '58673301815640',
      },
    },
  };

  // ar = width / height. File n lives at assets/photos/chemise-vague-NN.jpg (+ -800.jpg)
  const PHOTOS = [
    { n: '01', w: 1200, h: 1600, color: 'blanc', alt: 'La Chemise Blanche portée avec une jupe-short en jean, patte de boutonnage ondulée' },
    { n: '02', w: 1200, h: 1600, color: 'blanc', alt: 'La Chemise Blanche portée avec un jean brut, mains dans les poches' },
    { n: '03', w: 1600, h: 1535, color: 'blanc', alt: 'La Chemise Blanche portée ouverte sur un t-shirt vert : patte de boutonnage ondulée et poches asymétriques' },
    { n: '04', w: 1200, h: 1600, color: 'blanc', alt: 'La patte de boutonnage ondulée en train de se fermer, chemise blanche et short bleu marine' },
    { n: '05', w: 1200, h: 1600, color: 'blanc', alt: 'La Chemise Blanche portée ouverte sur un t-shirt rose' },
    { n: '06', w: 1200, h: 1600, color: 'blanc', alt: 'Détail d’une poche et d’une broderie soleil vert, motif de ferronnerie, sur le jacquard blanc' },
    { n: '07', w: 1200, h: 1599, color: 'blanc', alt: 'Détail du col et du jacquard de coton à motif floral ton sur ton' },
    { n: '08', w: 1199, h: 1600, color: 'raye', alt: 'Chemise Vague en version rayée lilas' },
  ];
  // on Shopify the theme's files sit in one flat folder, given by layout/theme.liquid (window.IAZ_ASSETS) ; locally, assets/photos/
  const PHOTO_DIR = window.IAZ_ASSETS || 'assets/photos/';
  PHOTOS.forEach((p) => { p.ar = p.w / p.h; p.src = `${PHOTO_DIR}chemise-vague-${p.n}.jpg`; p.src800 = `${PHOTO_DIR}chemise-vague-${p.n}-800.jpg`; });

  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
  const fmt2 = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(n);
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const isTouch = matchMedia('(pointer: coarse)').matches;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  // the motion tokens of the CSS (--dur, --ease) : one duration, one ease-out curve for every animation
  const rootCss = getComputedStyle(document.documentElement);
  const DUR = parseFloat(rootCss.getPropertyValue('--dur')) * 1000 || 500, EASE = rootCss.getPropertyValue('--ease').trim() || 'ease-out';
  // two columns (tablet, desktop) vs one (phones) : same query as the CSS
  const wideMq = matchMedia('(min-width: 700px) and (min-height: 500px)');
  const desktop = () => wideMq.matches;

  const store = {
    get(k, d) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } },
  };
  const state = { color: store.get('shop.color2', 'blanc'), size: 'M', cart: store.get('shop.cart2', []) };
  if (!PRODUCT.colors.some((c) => c.id === state.color)) state.color = PRODUCT.colors[0].id;
  state.cart = state.cart.filter((l) => PRODUCT.colors.some((c) => c.id === l.color));   // a cart saved when another colour was on sale
  const colorOf = (id) => PRODUCT.colors.find((c) => c.id === id) || PRODUCT.colors[0];

  const el = {
    body: document.body, swatches: $('#swatches'), sizes: $('#sizes'), colorName: $('#colorName'),
    add: $('#addBtn'), addLabel: $('#addLabel'),
    cartBtn: $('#cartBtn'), cartCount: $('#cartCount'), toast: $('#toast'),
    drawer: $('#drawer'), drawerTitle: $('#drawerTitle'), drawerBody: $('#drawerBody'), drawerFoot: $('#drawerFoot'), scrim: $('#scrim'),
    scroll: $('#pdpScroll'), pdp: $('#pdp'), top: $('.top'), home: $('#home'), help: $('#help'),
    gallery: $('#gallery'), dots: $('#dots'), thumbs: $('#thumbs'),
    lb: $('#lb'), lbImg: $('#lbImg'),
  };

  /* ================================================================== */
  /* Gallery                                                             */
  /* ================================================================== */

  // each colour is its own product : the gallery only shows the photos of the chosen colour.
  // One strip of photos on every screen, one photo per frame : swiped on phones ; from 700 px, arrows on the photo, and from 1000 px
  // a column of thumbnails beside it. A click on the photo opens it full screen.
  let list = [], shown = 0;
  function buildGallery() {
    const col = $('#colA');
    col.innerHTML = ''; el.thumbs.innerHTML = ''; list = PHOTOS.filter((p) => p.color === state.color);
    list.forEach((p, i) => {
      const fig = document.createElement('figure');
      fig.className = 'shot'; fig.dataset.i = i; fig.style.setProperty('--ar', p.ar.toFixed(4));
      fig.innerHTML = `<button type="button" class="shot__btn" aria-label="Agrandir la photo ${i + 1} sur ${list.length}">
        <span class="shot__frame"><img src="${p.src800}" srcset="${p.src800} 800w, ${p.src} ${p.w}w" sizes="(min-width: 1000px) 40vw, (min-width: 700px) 54vw, 100vw"
          alt="${p.alt}" width="${p.w}" height="${p.h}" ${i < 2 ? 'fetchpriority="high"' : 'loading="lazy"'} decoding="async" draggable="false"></span></button>`;
      fig.querySelector('.shot__btn').addEventListener('click', () => openLb(i, fig.querySelector('.shot__frame')));
      col.appendChild(fig);
      const t = document.createElement('button');
      t.type = 'button'; t.className = 'thumb'; t.dataset.i = i; t.setAttribute('aria-label', `Photo ${i + 1} sur ${list.length}`);
      t.innerHTML = `<img src="${p.src800}" alt="" width="${p.w}" height="${p.h}" loading="lazy" decoding="async" draggable="false">`;
      t.addEventListener('click', () => focusPhoto(i));
      el.thumbs.appendChild(t);
    });
    el.dots.innerHTML = list.map(() => '<i></i>').join('');
    el.gallery.classList.toggle('is-single', list.length < 2);
    el.gallery.scrollLeft = 0; shown = 0; markPhoto();
  }

  // the photo in view : its dot, its thumbnail
  function markPhoto() {
    $$('i', el.dots).forEach((d, j) => d.classList.toggle('is-on', j === shown));
    $$('.thumb', el.thumbs).forEach((t, j) => (j === shown ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
  }
  function onGalleryScroll() {
    const i = Math.round(el.gallery.scrollLeft / Math.max(1, el.gallery.clientWidth));
    if (i !== shown) { shown = clamp(i, 0, list.length - 1); markPhoto(); }
  }
  el.gallery.addEventListener('scroll', onGalleryScroll, { passive: true });

  function focusPhoto(i) {
    const n = (i + list.length) % list.length, fig = $(`.shot[data-i="${n}"]`);
    if (!fig) return;
    shown = n; markPhoto();
    el.gallery.scrollTo({ left: fig.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
  }
  $('#galPrev').addEventListener('click', () => focusPhoto(shown - 1));
  $('#galNext').addEventListener('click', () => focusPhoto(shown + 1));

  /* ================================================================== */
  /* Lightbox (FLIP from the thumbnail, hover-to-zoom)                   */
  /* ================================================================== */

  const lb = { open: false, i: 0, zoom: false, w: 0, h: 0, tx: 0, ty: 0, ttx: 0, tty: 0, raf: 0, anim: null };
  function lbFit(p) {
    const W = innerWidth, H = innerHeight, mobile = !desktop();
    const aw = W - (mobile ? 24 : 160), ah = H - (mobile ? 90 : 72);
    const s = Math.min(aw / p.w, ah / p.h);
    return { w: Math.round(p.w * s), h: Math.round(p.h * s) };
  }
  function lbSet(i) {
    const p = list[i];
    lb.i = i; const f = lbFit(p); lb.w = f.w; lb.h = f.h;
    el.lbImg.src = p.src; el.lbImg.srcset = ''; el.lbImg.alt = p.alt;
    el.lbImg.style.width = f.w + 'px'; el.lbImg.style.height = f.h + 'px';
    lbZoom(false);
  }
  function openLb(i, fromEl) {
    if (lb.open) return;
    lb.open = true; el.lb.hidden = false;
    $('#lbStage').style.setProperty('--hold', '0');
    lbSet(i);
    el.lb.classList.add('is-open');
    el.lbImg.style.opacity = '0';
    const place = () => {
      const dst = el.lbImg.getBoundingClientRect(), src = fromEl.getBoundingClientRect();
      el.lbImg.style.opacity = '1';
      if (reduceMotion) return;
      const sx = src.width / dst.width, dx = src.left - dst.left, dy = src.top - dst.top;
      if (lb.anim) lb.anim.cancel();
      lb.anim = el.lbImg.animate([{ transformOrigin: '0 0', transform: `translate(${dx}px, ${dy}px) scale(${sx})` }, { transformOrigin: '0 0', transform: 'translate(0,0) scale(1)' }], { duration: DUR, easing: EASE });
    };
    requestAnimationFrame(place);
    $$('.pdp, .top').forEach((n) => n.setAttribute('inert', ''));
    setTimeout(() => $('#lbClose').focus({ preventScroll: true }), 80);
  }
  function closeLb() {
    if (!lb.open) return;
    lb.open = false; lbZoom(false);
    const fig = $(`.shot[data-i="${lb.i}"]`); if (fig) el.gallery.scrollLeft = fig.offsetLeft;   // the strip shows the photo the viewer ended on
    const thumb = $(`.shot[data-i="${lb.i}"] .shot__frame`);
    const dst = el.lbImg.getBoundingClientRect(), src = thumb ? thumb.getBoundingClientRect() : null;
    el.lb.classList.remove('is-open');
    const finish = () => { el.lb.hidden = true; el.lbImg.removeAttribute('src'); };
    if (src && !reduceMotion) {
      const sx = src.width / dst.width, dx = src.left - dst.left, dy = src.top - dst.top;
      lb.anim = el.lbImg.animate([{ transformOrigin: '0 0', transform: 'translate(0,0) scale(1)' }, { transformOrigin: '0 0', transform: `translate(${dx}px, ${dy}px) scale(${sx})` }], { duration: DUR, easing: EASE, fill: 'forwards' });
      lb.anim.onfinish = finish;
    } else finish();
    $$('.pdp, .top').forEach((n) => n.removeAttribute('inert'));
    applyInert();
    const btn = $(`.shot[data-i="${lb.i}"] .shot__btn`); if (btn) btn.focus({ preventScroll: true });
  }
  function lbGo(d) {
    if (!lb.open) return;
    const n = (lb.i + d + list.length) % list.length;
    lbSet(n);                                                      // a cut, no fade
  }
  function lbZoom(on) {
    lb.zoom = on; el.lb.classList.toggle('is-zoom', on);
    if (!on) { lb.tx = lb.ty = lb.ttx = lb.tty = 0; el.lbImg.style.transform = ''; cancelAnimationFrame(lb.raf); lb.raf = 0; return; }
    if (!lb.raf) lb.raf = requestAnimationFrame(lbLoop);
  }
  function lbLoop() {
    lb.raf = 0;
    if (!lb.zoom) return;
    lb.tx += (lb.ttx - lb.tx) * 0.2; lb.ty += (lb.tty - lb.ty) * 0.2;
    el.lbImg.style.transform = `translate3d(${lb.tx.toFixed(1)}px, ${lb.ty.toFixed(1)}px, 0) scale(${LB_Z})`;
    lb.raf = requestAnimationFrame(lbLoop);
  }
  const LB_Z = 2.6;
  function lbPan(x, y) {
    const r = el.lbImg.getBoundingClientRect(), s = lb.zoom ? LB_Z : 1;
    const cx = r.left + r.width / 2 - lb.tx, cy = r.top + r.height / 2 - lb.ty;           // untransformed centre
    const mx = (lb.w * (LB_Z - 1)) / 2, my = (lb.h * (LB_Z - 1)) / 2;
    lb.ttx = clamp(-(x - cx) * (LB_Z - 1), -mx, mx); lb.tty = clamp(-(y - cy) * (LB_Z - 1), -my, my);
  }
  let lbDown = null;
  $('#lbStage').addEventListener('pointerdown', (e) => { lbDown = { x: e.clientX, y: e.clientY, t: performance.now(), moved: 0, id: e.pointerId }; });
  $('#lbStage').addEventListener('pointermove', (e) => {
    if (lb.zoom && (!isTouch || lbDown)) {
      if (isTouch && lbDown) { lb.ttx += e.movementX * 1.2; lb.tty += e.movementY * 1.2; } else lbPan(e.clientX, e.clientY);
    }
    if (lbDown) lbDown.moved = Math.max(lbDown.moved, Math.hypot(e.clientX - lbDown.x, e.clientY - lbDown.y));
  });
  $('#lbStage').addEventListener('pointerup', (e) => {
    if (!lbDown) return;
    const dx = e.clientX - lbDown.x, moved = lbDown.moved; lbDown = null;
    if (!lb.zoom && isTouch && Math.abs(dx) > 60) return lbGo(dx < 0 ? 1 : -1);      // swipe
    if (moved < 8) { if (!lb.zoom) lbPan(e.clientX, e.clientY); lbZoom(!lb.zoom); }
  });
  $('#lbClose').addEventListener('click', closeLb);
  $('#lbPrev').addEventListener('click', () => lbGo(-1));
  $('#lbNext').addEventListener('click', () => lbGo(1));
  $('.lb__bg').addEventListener('click', closeLb);
  addEventListener('resize', () => { if (lb.open) lbSet(lb.i); });

  /* ================================================================== */
  /* Product UI                                                          */
  /* ================================================================== */

  function renderSwatches() {
    $('#colorField').hidden = PRODUCT.colors.length < 2;             // one colour : nothing to choose
    el.swatches.innerHTML = '';
    PRODUCT.colors.forEach((c) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'swatch'; b.setAttribute('role', 'radio');
      b.setAttribute('aria-label', c.label); b.dataset.color = c.id;
      b.innerHTML = `<img src="${PHOTOS[c.photo].src800}" alt="" loading="lazy" decoding="async" draggable="false">`;    // a small photo of the colour
      b.addEventListener('click', () => setColor(c.id));
      el.swatches.appendChild(b);
    });
    syncColor();
  }
  function setColor(id) {
    if (id === state.color) return;
    state.color = id; store.set('shop.color2', id);
    syncColor(); syncSizes(); buildGallery();
  }
  function syncColor() {
    $$('.swatch').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.color === state.color)));
    el.colorName.textContent = colorOf(state.color).label;
  }
  function renderSizes() {
    el.sizes.innerHTML = '';
    PRODUCT.sizes.forEach((s) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'size'; b.innerHTML = `<span>${s}</span>`; b.dataset.size = s; b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', String(s === state.size));
      b.addEventListener('click', () => setSize(s));
      el.sizes.appendChild(b);
    });
    el.sizes.insertAdjacentHTML('beforeend', '<i class="sizes__pick" aria-hidden="true"></i>');
    placePick();
    requestAnimationFrame(() => requestAnimationFrame(() => el.sizes.classList.add('is-ready')));   // no slide on the first paint
  }
  // the sliding block under the chosen size
  function placePick() {
    const i = PRODUCT.sizes.indexOf(state.size);
    el.sizes.style.setProperty('--i', Math.max(i, 0));
    el.sizes.dataset.pick = i < 0 ? 'none' : '';
  }
  function setSize(s) {
    if (isOut(state.color, s)) return;
    state.size = s;
    $$('.size').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.size === s)));
    placePick(); updateCta();
  }
  function updateCta() {
    const ready = !!state.size, gone = PRODUCT.sizes.every((s) => isOut(state.color, s));
    el.add.classList.toggle('is-idle', !ready);
    el.add.disabled = gone;
    el.addLabel.textContent = gone ? 'Épuisé' : ready ? 'Ajouter au panier' : 'Choisir une taille';
    $('#addPrice').hidden = gone;
    $('#stock').classList.toggle('is-out', gone);
    $('#stockLabel').textContent = gone ? 'Épuisé' : 'En stock';
  }

  // stock : the colour × size pairs Shopify no longer sells. Filled by loadStock ; empty = everything on sale
  // (Shopify still refuses a sold-out variant at checkout).
  const soldOut = new Set();
  const isOut = (color, size) => soldOut.has(`${color}:${size}`);
  function syncSizes() {
    $$('.size', el.sizes).forEach((b) => {
      const out = isOut(state.color, b.dataset.size);
      b.disabled = out; b.classList.toggle('is-out', out);
      b.setAttribute('aria-label', out ? `${b.dataset.size}, épuisé` : b.dataset.size);
    });
    if (!state.size || isOut(state.color, state.size)) state.size = ['M', ...PRODUCT.sizes].find((s) => !isOut(state.color, s)) || null;   // M by default, else the first size left
    $$('.size', el.sizes).forEach((b) => b.setAttribute('aria-checked', String(b.dataset.size === state.size)));
    placePick(); updateCta();
  }
  async function loadStock() {
    const { shop, handle, storefrontToken, apiVersion, variants } = PRODUCT.shopify;
    if (!storefrontToken) return;
    try {
      const res = await fetch(`https://${shop}/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': storefrontToken },
        body: JSON.stringify({ query: 'query($h: String!) { product(handle: $h) { variants(first: 50) { nodes { id availableForSale } } } }', variables: { h: handle } }),
      });
      const nodes = (await res.json())?.data?.product?.variants?.nodes;
      if (!nodes) return;
      const available = Object.fromEntries(nodes.map((n) => [n.id.split('/').pop(), n.availableForSale]));
      soldOut.clear();
      Object.entries(variants).forEach(([key, id]) => { if (available[id] === false) soldOut.add(key); });
      syncSizes();
    } catch (err) { /* offline or blocked : keep every size on sale */ }
  }
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') loadStock(); });
  let toastTimer = 0;
  function toast(msg) {
    el.toast.textContent = msg; el.toast.classList.add('is-on');
    clearTimeout(toastTimer); toastTimer = setTimeout(() => el.toast.classList.remove('is-on'), 2800);
  }

  const cartCount = () => state.cart.reduce((n, l) => n + l.qty, 0);
  const cartTotal = () => state.cart.reduce((n, l) => n + l.qty * PRODUCT.price, 0);
  function saveCart() { store.set('shop.cart2', state.cart); $$('[data-cart-count]').forEach((n) => { n.textContent = cartCount(); }); }
  const replay = (node, cls) => { node.classList.remove(cls); void node.offsetWidth; node.classList.add(cls); };

  function addToCart() {
    if (state.size && isOut(state.color, state.size)) return toast('Cette taille est épuisée');
    if (!state.size) {
      replay(el.sizes, 'is-nudge');
      const f = $('#sizeField'), sr = el.scroll.getBoundingClientRect(), r = f.getBoundingClientRect();
      if (r.bottom < sr.top + 20 || r.top > sr.bottom - 20) f.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      toast('Choisissez une taille pour continuer');
      return;
    }
    const ex = state.cart.find((l) => l.color === state.color && l.size === state.size);
    if (ex) ex.qty = Math.min(9, ex.qty + 1); else state.cart.push({ id: PRODUCT.id, color: state.color, size: state.size, qty: 1 });
    saveCart();
    $$('[data-cart]').forEach((b) => replay(b, 'is-bump'));
    el.addLabel.textContent = 'Ajouté';                            // the label says it, the button keeps its colours
    setTimeout(updateCta, 1500);
    setTimeout(() => openDrawer('cart', el.add), 520);
  }
  el.add.addEventListener('click', addToCart);

  $$('.acc__btn').forEach((b) => {
    const panel = $('#' + b.getAttribute('aria-controls'));
    const sync = () => panel.classList.toggle('is-open', b.getAttribute('aria-expanded') === 'true');
    sync();
    b.addEventListener('click', () => { b.setAttribute('aria-expanded', String(b.getAttribute('aria-expanded') !== 'true')); sync(); });
  });

  // details : the tabs on the left show their accordions on the right (arrow keys move between the tabs)
  const tabs = $$('.details__tab');
  function pickTab(t, focus) {
    tabs.forEach((x) => {
      const on = x === t;
      x.setAttribute('aria-selected', String(on)); x.tabIndex = on ? 0 : -1;
      $('#' + x.getAttribute('aria-controls')).hidden = !on;
    });
    if (focus) t.focus();
  }
  tabs.forEach((t, i) => {
    t.addEventListener('click', () => pickTab(t));
    t.addEventListener('keydown', (e) => {
      const d = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
      if (d) { e.preventDefault(); pickTab(tabs[(i + d + tabs.length) % tabs.length], true); }
    });
  });

  /* ================================================================== */
  /* Drawer (cart + size guide)                                          */
  /* ================================================================== */

  const thumbOf = (colorId) => { const c = colorOf(colorId); return PHOTOS[c.photo].src800; };
  let drawerOpener = null, drawerKind = null;
  const inertTargets = () => [$('.top'), ...Object.values(PAGES)];

  function openDrawer(kind, opener) {
    if (!drawerKind) drawerOpener = opener || document.activeElement;   // a drawer opened from another one (menu → newsletter) keeps the first opener
    drawerKind = kind;
    renderDrawer();
    el.scrim.hidden = false; el.drawer.hidden = false;
    requestAnimationFrame(() => { el.scrim.classList.add('is-open'); el.drawer.classList.add('is-open'); });
    inertTargets().forEach((n) => n.setAttribute('inert', ''));
    setTimeout(() => $('#drawerClose').focus({ preventScroll: true }), 80);
  }
  function closeDrawer() {
    if (!drawerKind) return;
    drawerKind = null;
    el.scrim.classList.remove('is-open'); el.drawer.classList.remove('is-open');
    inertTargets().forEach((n) => n.removeAttribute('inert'));
    applyInert();
    setTimeout(() => { if (!drawerKind) { el.scrim.hidden = true; el.drawer.hidden = true; } }, DUR);
    if (drawerOpener && drawerOpener.focus) drawerOpener.focus({ preventScroll: true });
  }
  function renderDrawer() {
    if (drawerKind === 'cart') {
      el.drawerTitle.textContent = 'Panier';
      if (!state.cart.length) {
        el.drawerBody.innerHTML = `<div class="empty"><b>Votre panier est vide.</b>Une chemise vous attend.</div>`;
        el.drawerFoot.innerHTML = `<button type="button" class="cta cta--ghost" data-act="close"><span>Continuer mes achats</span></button>`;
        return;
      }
      el.drawerBody.innerHTML = state.cart.map((l, i) => {
        const c = colorOf(l.color);
        return `<div class="line" data-i="${i}">
          <div class="line__img"><img src="${thumbOf(l.color)}" alt="" loading="lazy"></div>
          <div><div class="line__name">${PRODUCT.name}</div><div class="line__meta">${c.label} · Taille ${l.size}</div>
            <div class="qty"><button type="button" data-act="dec" aria-label="Retirer un">−</button><span aria-live="polite">${l.qty}</span><button type="button" data-act="inc" aria-label="Ajouter un">+</button></div>
            <button type="button" class="line__rm" data-act="rm">Retirer</button></div>
          <div class="line__price">${fmt(l.qty * PRODUCT.price)}</div></div>`;
      }).join('');
      // shipping is Shopify's : "frais de port compris" only holds if its free-shipping thresholds are at or under 90 € (France 65 €, EU 100 € for now : see shopify/A-FAIRE.md)
      const total = cartTotal();
      el.drawerFoot.innerHTML = `
        <div class="sum"><span>Sous-total</span><span>${fmt2(total)}</span></div>
        <div class="sum"><span>Livraison</span><span>Frais de port compris</span></div>
        <div class="sum sum--total"><span>Total</span><span>${fmt2(total)}</span></div>
        <p class="ship">Livraison en France et dans l’Union européenne, expédiée sous 48 h.</p>
        <button type="button" class="cta" id="checkout"><span>Commander</span><span class="cta__right">${fmt2(total)}</span></button>
        <button type="button" class="cta cta--ghost" data-act="close"><span>Continuer mes achats</span></button>`;
    } else if (drawerKind === 'sizes' || drawerKind === 'fit') {
      const fit = drawerKind === 'fit', col = PRODUCT.sizes.indexOf(state.size);
      const pick = (i) => (i === col ? ' class="is-pick"' : '');
      const tips = `<div class="tips">
        <div><b>Homme</b><p>Prenez votre taille habituelle, voire une taille au-dessus pour accentuer l’effet oversize.</p></div>
        <div><b>Femme</b><p>Selon l’effet souhaité&#8239;: votre taille habituelle pour l’effet oversize, ou une taille en dessous pour une coupe plus droite.</p></div></div>`;
      const table = `<table class="sz"><thead><tr><th>Mesure (cm)</th>${PRODUCT.sizes.map((s, i) => `<th${pick(i)}>${s}</th>`).join('')}</tr></thead><tbody>
        ${PRODUCT.sizeTable.map((r) => `<tr><td>${r.label}${r.note ? `<small>${r.note}</small>` : ''}</td>${r.v.map((v, i) => `<td${pick(i)}>${v}</td>`).join('')}</tr>`).join('')}
        </tbody></table>`;
      el.drawerTitle.textContent = fit ? 'Trouver ma taille' : 'Guide des tailles';
      el.drawerBody.innerHTML = fit
        ? tips + `<p class="lead">Un doute&#8239;? Posez à plat une chemise qui vous va bien, mesurez-la et comparez avec ce tableau (mesures du vêtement à plat, en centimètres).</p>` + table
          + `<p class="lead">Besoin d’un conseil taille&#8239;? Écrivez-nous à <a href="mailto:hello@iazstudio.com">hello@iazstudio.com</a>.</p>`
        : `<p class="lead">Mesures du vêtement à plat, en centimètres.</p>` + table + tips;
      el.drawerFoot.innerHTML = '';
    } else if (drawerKind === 'menu') {
      const row = (href, label, on) => `<a href="${href}"${on ? ' aria-current="page"' : ''}><span>${label}</span><svg class="i" aria-hidden="true"><use href="#i-caret-right"/></svg></a>`;
      el.drawerTitle.textContent = 'Menu';
      el.drawerBody.innerHTML = `<nav class="menu" aria-label="Catégories">
        ${row('/', 'Accueil', current === 'home')}${row('/products/chemise-vague', 'La chemise', current === 'product')}${row('/#marque', 'La marque')}${row('/#newsletter', 'Newsletter')}${row('/pages/aide', 'Besoin d’aide&#8239;?', current === 'help')}</nav>`;
      el.drawerFoot.innerHTML = '';
    } else if (drawerKind === 'newsletter') {
      el.drawerTitle.textContent = 'Newsletter';
      el.drawerBody.innerHTML = `<p class="lead">Les autres pièces arrivent, doucement mais sûrement. Pour être tenu·e au courant des nouveautés, inscrivez-vous. Promis, nous ne noierons pas votre boîte mail&#8239;: nous communiquons uniquement si c’est important.</p><div data-newsletter></div>`;
      mountNewsletters(el.drawerBody);
      el.drawerFoot.innerHTML = '';
    }
  }

  // the shop's address : nothing on the theme (same domain, window.Shopify is set by Shopify), the myshopify address locally
  const SHOP_URL = window.Shopify ? '' : `https://${PRODUCT.shopify.shop}`;

  // newsletter : the browser posts the form to Shopify (customer form, tag "newsletter") in a new tab.
  // Not tested end to end : the shop is still behind its password, and a test would create a real customer.
  function mountNewsletters(root) {
    $$('[data-newsletter]', root).forEach((m) => {
      if (m.firstElementChild) return;
      m.appendChild($('#tplNews').content.cloneNode(true));
      m.querySelector('form').action = `${SHOP_URL}/contact#contact_form`;
    });
  }
  document.addEventListener('submit', (e) => {
    const f = e.target.closest('.news');
    if (f) $('.news__msg', f).textContent = 'Votre inscription s’ouvre dans un nouvel onglet.';
    if (e.target.id === 'contactForm') $('.contact__msg', e.target).textContent = 'Votre message s’envoie dans un nouvel onglet.';
  });

  // a field the browser refuses on submit (empty, not an e-mail) gives a small shake
  document.addEventListener('invalid', (e) => { const f = e.target.closest('.fld'); if (f) replay(f, 'is-nudge'); }, true);

  // help page : the message form posts to Shopify's contact form, in a new tab, like the newsletter (same caveat : not tested end to end) ;
  // "Mon compte" opens the Shopify customer account, where the orders and their tracking are
  $('#contactForm').action = `${SHOP_URL}/contact#contact_form`;
  $('[data-account]').href = `${SHOP_URL}/account`;

  // checkout : hand the cart to Shopify, which takes care of address, shipping and payment
  function checkout(btn) {
    const { variants } = PRODUCT.shopify;
    const lines = state.cart.map((l) => variants[`${l.color}:${l.size}`] && `${variants[`${l.color}:${l.size}`]}:${l.qty}`);
    if (!lines.length || lines.some((x) => !x)) return toast('Cet article n’est pas disponible pour le moment');
    btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true');
    location.assign(`${SHOP_URL}/cart/${lines.join(',')}`);
  }
  addEventListener('pageshow', () => { const b = $('#checkout'); if (b) { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); } });   // back from Shopify
  el.drawer.addEventListener('click', (e) => {
    const t = e.target.closest('[data-act], #checkout, #drawerClose, a[href^="/"]');
    if (!t) return;
    if (t.tagName === 'A') return closeDrawer();
    if (t.id === 'drawerClose' || t.dataset.act === 'close') return closeDrawer();
    if (t.id === 'checkout') return checkout(t);
    const i = +t.closest('.line').dataset.i, l = state.cart[i];
    if (t.dataset.act === 'inc') l.qty = Math.min(9, l.qty + 1);
    if (t.dataset.act === 'dec') l.qty -= 1;
    if (t.dataset.act === 'rm') l.qty = 0;
    if (l.qty <= 0) state.cart.splice(i, 1);
    saveCart(); renderDrawer();
  });
  el.scrim.addEventListener('click', closeDrawer);
  $$('[data-cart]').forEach((b) => b.addEventListener('click', (e) => openDrawer('cart', e.currentTarget)));
  document.addEventListener('click', (e) => { const b = e.target.closest('[data-panel]'); if (b) openDrawer(b.dataset.panel, b); });

  /* ================================================================== */
  /* Routing                                                             */
  /* ================================================================== */

  // three pages, cut in and out : the home (one page : hero, La marque, the shirt, newsletter), the product sheet and the help page.
  // One address each, Shopify's : /  /products/chemise-vague  /pages/aide (their title and description live in index.html, script#pages ;
  // dev/build-theme.js writes them into the theme). The hash is an anchor : on the home  #galerie  #marque  #communaute  #newsletter ; on the help page  #faq  #message  #retour
  const META = JSON.parse($('#pages').textContent);
  const PAGES = { home: el.home, product: el.pdp, help: el.help };
  const cleanPath = (p) => p.replace(/\.html$/, '').replace(/\/index$/, '/').replace(/(.)\/$/, '$1');
  const routeOf = () => ({ '/products/chemise-vague': 'product', '/pages/aide': 'help', '/chemise': 'product', '/aide': 'help' }[cleanPath(location.pathname)] || 'home');
  const anchor = () => location.hash.slice(1);
  // the old addresses (#/chemise, #/aide/retour, #/marque …) still work : they become the new ones
  if (location.hash.startsWith('#/')) {
    const [p, sub] = location.hash.slice(2).split('/');
    history.replaceState(null, '', p === 'chemise' ? '/products/chemise-vague' : p === 'aide' ? '/pages/aide' + (sub ? '#' + sub : '') : '/' + (p ? '#' + p : ''));
  }
  // the page's title, description, canonical and share card follow the page
  const setMeta = (sel, attr, v) => { const n = document.querySelector(sel); if (n) n.setAttribute(attr, v); };
  function applyMeta(r) {
    const m = META[r], url = 'https://iazstudio.com' + m.path;
    document.title = m.title;
    setMeta('meta[name="description"]', 'content', m.description);
    setMeta('link[rel="canonical"]', 'href', url);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:title"]', 'content', m.ogTitle);
    setMeta('meta[property="og:description"]', 'content', m.description);
    setMeta('meta[property="og:type"]', 'content', m.ogType);
  }
  let current = null;

  function applyInert() {
    Object.entries(PAGES).forEach(([r, n]) => n.toggleAttribute('inert', r !== current || (r === 'product' && lb.open)));
  }

  // the sections of the home that the header follows
  const sec = { hero: $('#hero'), about: $('#marque'), feature: $('#feature'), foot: $('#newsletter') };
  const markNav = (now) => $$('[data-nav]').forEach((a) => (a.dataset.nav === now ? a.setAttribute('aria-current', 'page') : a.removeAttribute('aria-current')));
  // home : the header is clear over the hero and turns into a cream band once the hero has gone up under it ; the category of the section in view is underlined.
  // product : on phones the header band leaves with the page, as if it were part of it
  function syncScrolled() {
    const sy = current === 'product' ? el.scroll.scrollTop : 0;
    el.top.style.translate = sy > 0 && !desktop() ? `0 ${-Math.min(sy, el.top.offsetHeight)}px` : '';
    if (current === 'home') {
      const band = el.top.offsetHeight, line = band + 24, past = (n) => n.getBoundingClientRect().top <= line;
      el.top.classList.toggle('is-solid', sec.hero.getBoundingClientRect().bottom <= band);
      markNav(past(sec.about) && !past(sec.feature) ? 'brand' : 'home');
    } else {
      el.top.classList.remove('is-solid');
      markNav(current);
    }
  }
  el.home.addEventListener('scroll', syncScrolled, { passive: true });
  el.scroll.addEventListener('scroll', syncScrolled, { passive: true });
  addEventListener('resize', syncScrolled);
  // the element an anchor points at : #marque, #newsletter
  const homeTarget = () => ({ marque: sec.about, galerie: $('#galerie'), communaute: $('#communaute'), newsletter: sec.foot }[anchor()] || null);
  // the help page : #faq lands on the questions ; #message and #retour on the form, retour picks the subject
  function helpTarget() {
    const sub = anchor();
    if (sub === 'retour') $('#contactSubject').value = 'Retour ou échange';
    return sub === 'faq' ? $('#faq') : sub === 'message' || sub === 'retour' ? $('#helpForm') : null;
  }
  function scrollToAnchor(fresh) {
    if (current === 'product') return;
    const page = PAGES[current], target = current === 'home' ? homeTarget() : helpTarget(), behavior = fresh || reduceMotion ? 'auto' : 'smooth';
    if (target) {
      requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior }));
    } else if (!fresh) page.scrollTo({ top: 0, behavior });           // / again : back to the top (coming back from the product, the page stays where it was)
  }
  function applyRoute() {
    const r = routeOf();
    const changed = r !== current;
    current = r;
    el.body.dataset.route = r;
    applyMeta(r);
    applyInert();
    if (changed) {
      // pages cut in and out : the header changes colours between the two, without the usual easing (one cut, no fade)
      el.body.classList.add('is-cut'); requestAnimationFrame(() => requestAnimationFrame(() => el.body.classList.remove('is-cut')));
      if (r === 'product') { el.scroll.scrollTop = 0; el.gallery.scrollLeft = 0; $('#info').scrollTop = 0; }
      if (r === 'help') el.help.scrollTop = 0;
      if (drawerKind) closeDrawer();
      if (lb.open) closeLb();
    }
    syncScrolled();
    scrollToAnchor(changed);
  }
  // the site's own links change the page without reloading it (History API) ; back and forward follow
  function navigate(href) {
    const u = new URL(href, location.href);
    history.pushState(null, '', cleanPath(u.pathname) + u.hash);
    applyRoute();
  }
  const go = (r) => navigate(META[r].path);
  addEventListener('popstate', applyRoute);
  addEventListener('hashchange', applyRoute);
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="/"]');
    if (!a || a.target || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
    e.preventDefault();
    const same = cleanPath(a.pathname) === cleanPath(location.pathname) && a.hash === location.hash;
    // the same address again : scroll to its anchor (or back to the top) by hand
    if (same) { if (current !== 'product') scrollToAnchor(false); return; }
    // from the home, "La chemise" (header, hero button, photo) goes through the burgundy cut, like a click on the shirt
    if (cleanPath(a.pathname) === '/products/chemise-vague' && current === 'home' && !reduceMotion) return toProduct();
    navigate(a.href);
  });
  addEventListener('keydown', (e) => {
    if (lb.open) {
      if (e.key === 'Escape') closeLb(); else if (e.key === 'ArrowRight') lbGo(1); else if (e.key === 'ArrowLeft') lbGo(-1);
      return;
    }
    if (e.key === 'Escape') { if (drawerKind) closeDrawer(); else if (current !== 'home') go('home'); }
  });


  /* ================================================================== */
  /* Boot                                                                */
  /* ================================================================== */

  buildGallery(); renderSwatches(); renderSizes(); syncSizes(); saveCart(); loadStock();
  current = routeOf(); el.body.dataset.route = current; applyInert();
  applyMeta(current);
  mountNewsletters(document); syncScrolled(); scrollToAnchor(true);
  onGalleryScroll();

  // cookies : the band shows until a choice is made ; the choice is kept ("all" | "required"). The site needs nothing beyond the
  // required ones today : a tracker added later must read window.iazConsent first, or wait for the "iaz:consent" event.
  // On Shopify (the theme), Shopify's own banner asks instead : it governs Shopify's cookies (statistics, Shop Pay), so ours stays hidden
  // and "Cookies" in the footers opens Shopify's preferences.
  const consent = $('#consent');
  if (window.Shopify) {
    $$('[data-consent-open]').forEach((b) => b.addEventListener('click', () => window.privacyBanner && window.privacyBanner.showPreferences()));
  } else {
    window.iazConsent = store.get('shop.consent', null);
    if (!window.iazConsent) consent.hidden = false;
    consent.addEventListener('click', (e) => {
      const b = e.target.closest('[data-consent]'); if (!b) return;
      window.iazConsent = b.dataset.consent; store.set('shop.consent', b.dataset.consent);
      consent.hidden = true;
      dispatchEvent(new CustomEvent('iaz:consent', { detail: b.dataset.consent }));
    });
    $$('[data-consent-open]').forEach((b) => b.addEventListener('click', () => { consent.hidden = false; $('[data-consent]', consent).focus(); }));
  }

  // the shirt section : with a mouse the cursor becomes the "Commander maintenant" label, which trails the pointer with a small delay
  // (it eases toward it a little more each frame) ; a click anywhere on the section opens the product. On touch, the photo and the button do.
  const feature = $('#feature'), order = $('#featureOrder'), hoverMq = matchMedia('(hover: hover)');
  const LAG = 0.14;                                                 // share of the distance covered per frame at 60 fps (smaller = longer delay)
  const cur = { x: 0, y: 0 }, tgt = { x: 0, y: 0 };
  let raf = 0, seen = false, last = 0;
  const drawLabel = () => { feature.style.setProperty('--mx', cur.x.toFixed(1) + 'px'); feature.style.setProperty('--my', cur.y.toFixed(1) + 'px'); };
  function trail(t) {
    const k = 1 - Math.pow(1 - LAG, Math.min(t - last, 64) / 16.67); last = t;   // same feel at 60, 120 or 144 Hz
    cur.x += (tgt.x - cur.x) * k; cur.y += (tgt.y - cur.y) * k;
    drawLabel();
    raf = Math.abs(tgt.x - cur.x) + Math.abs(tgt.y - cur.y) > 0.1 ? requestAnimationFrame(trail) : 0;
  }
  // tracked on the whole home, so the label is already under the pointer when the section scrolls under it
  el.home.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse') return;
    const hw = order.offsetWidth / 2 + 8, hh = order.offsetHeight / 2 + 8;
    tgt.x = clamp(e.clientX, hw, innerWidth - hw); tgt.y = clamp(e.clientY, hh, innerHeight - hh);
    if (!seen || reduceMotion) { cur.x = tgt.x; cur.y = tgt.y; seen = true; feature.classList.add('is-tracked'); drawLabel(); }      // first appearance : no flight from the corner
    if (!raf && !reduceMotion) { last = performance.now(); raf = requestAnimationFrame(trail); }
  }, { passive: true });
  el.home.addEventListener('pointerleave', () => { seen = false; feature.classList.remove('is-tracked'); });     // it reappears right under the pointer
  feature.addEventListener('click', (e) => {
    if (!hoverMq.matches || e.target.closest('a[href^="/"]') || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;   // the links do their own job
    if (reduceMotion) go('product'); else toProduct();
  });

  // home → product : the burgundy cuts in, the colour's photos appear one by one as small thumbnails while the page gets ready
  // (at least the time for every thumbnail to appear, at most 2.5 s for the first photos), then the page cuts in
  const veil = $('#veil'), veilRow = $('#veilRow');
  const wait = (ms) => new Promise((ok) => setTimeout(ok, ms));
  const imgReady = (im) => (im.complete && im.naturalWidth ? Promise.resolve()
    : new Promise((ok) => { im.addEventListener('load', ok, { once: true }); im.addEventListener('error', ok, { once: true }); }));
  let veiling = false;
  async function toProduct() {
    if (veiling) return; veiling = true;
    veilRow.innerHTML = list.map((p, i) => `<img src="${p.src800}" alt="" style="--i:${i}" decoding="async" draggable="false">`).join('');
    void veil.offsetWidth; veil.classList.add('is-on');
    go('product');                                                  // the product page switches in, under the veil
    const first = $$('.shot img').slice(0, 2);
    await Promise.all([wait(600 + list.length * 80), Promise.race([Promise.all(first.map(imgReady)), wait(2500)])]);
    veil.classList.remove('is-on'); veilRow.innerHTML = ''; veiling = false;
  }
  // then warm the cache for the product page
  addEventListener('load', () => PHOTOS.forEach((p) => { const im = new Image(); im.src = p.src800; }));
})();
